// ======================================================================
// File: src/modules/ipe/costing/pages/CostingViewPage.js
// ======================================================================

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ipeCostingService from "../../services/ipeCostingService";

export default function CostingViewPage() {

  const { enquiryId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionA, setSelectedVersionA] = useState(null);
  const [selectedVersionB, setSelectedVersionB] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {

    async function load() {

      try {

        const res = await ipeCostingService.getCostDashboard(enquiryId);
        setData(res || null);

        const ver = await ipeCostingService.getCostVersions(enquiryId);
        setVersions(ver || []);

        if (ver?.length > 0) setSelectedVersionA(ver[0]);
        if (ver?.length > 1) setSelectedVersionB(ver[1]);

      } finally {
        setLoading(false);
      }

    }

    load();

  }, [enquiryId]);

  if (loading) return <div>Loading costing…</div>;
  if (!data) return <div>No costing generated yet</div>;

  const summary = data.summary || {};
  const items = data.items || [];
  const materials = data.materials || [];
  const operations = data.operations || [];

  return (

    <div>

      {/* HEADER */}

      <div
        style={{
          background: "linear-gradient(90deg, #5f0cff, #7b2cff)",
          borderRadius: 14,
          padding: "16px 22px",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >

        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>
            Engineering Cost Sheet
          </div>

          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Enquiry ID : {enquiryId}
          </div>
        </div>

        <button
          onClick={() => navigate(`/ipe/enquiries/${enquiryId}`)}
          style={{
            background: "#fff",
            color: "#5f0cff",
            border: "none",
            borderRadius: 8,
            padding: "8px 14px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          ← Back to Enquiry
        </button>

      </div>


      {/* VERSION COMPARISON SELECTOR */}

      <div className="card mb-4">
        <div className="card-body">

          <h5 className="mb-3">Version Comparison</h5>

          <div className="row">

            <div className="col-md-6">

              <label>Version A</label>

              <select
                className="form-control"
                onChange={(e) =>
                  setSelectedVersionA(
                    versions.find(v => v.id === Number(e.target.value))
                  )
                }
              >
                {versions.map(v => (
                  <option key={v.id} value={v.id}>
                    Version {v.version_no} - {v.status}
                  </option>
                ))}
              </select>

            </div>

            <div className="col-md-6">

              <label>Version B</label>

              <select
                className="form-control"
                onChange={(e) =>
                  setSelectedVersionB(
                    versions.find(v => v.id === Number(e.target.value))
                  )
                }
              >
                {versions.map(v => (
                  <option key={v.id} value={v.id}>
                    Version {v.version_no} - {v.status}
                  </option>
                ))}
              </select>

            </div>

          </div>

        </div>
      </div>


      {/* VERSION COMPARISON TABLE */}

      <div className="card mb-4">
        <div className="card-body">

          <h5 className="mb-3">Version Cost Comparison</h5>

          <table className="table table-bordered">

            <thead>
              <tr>
                <th>Field</th>
                <th>Version {selectedVersionA?.version_no}</th>
                <th>Version {selectedVersionB?.version_no}</th>
              </tr>
            </thead>

            <tbody>

              <tr>
                <td>Manufacturing Cost</td>
                <td>{selectedVersionA?.manufacturing_cost}</td>
                <td>{selectedVersionB?.manufacturing_cost}</td>
              </tr>

              <tr>
                <td>Selling Price</td>
                <td>{selectedVersionA?.selling_price}</td>
                <td>{selectedVersionB?.selling_price}</td>
              </tr>

              <tr>
                <td>Final Price</td>
                <td>{selectedVersionA?.final_price}</td>
                <td>{selectedVersionB?.final_price}</td>
              </tr>

            </tbody>

          </table>

        </div>
      </div>


      {/* COST VERSION HISTORY */}

      <div className="card mb-4">
        <div className="card-body">

          <h5 className="mb-3">Cost Version History</h5>

          <table className="table table-bordered">

            <thead>
              <tr>
                <th>Version</th>
                <th>Status</th>
                <th>Manufacturing Cost</th>
                <th>Selling Price</th>
                <th>Final Price</th>
                <th>Created By</th>
                <th>Created At</th>
                <th>Updated By</th>
                <th>Updated At</th>
              </tr>
            </thead>

            <tbody>

              {versions.map((v, idx) => (

                <tr key={idx}>

                  <td><b>V{v.version_no}</b></td>

                  <td>
                    <span
                      className={`badge ${
                        v.status === "draft"
                          ? "bg-secondary"
                          : v.status === "submitted"
                          ? "bg-warning text-dark"
                          : v.status === "approved"
                          ? "bg-success"
                          : "bg-danger"
                      }`}
                    >
                      {v.status}
                    </span>
                  </td>

                  <td>{v.manufacturing_cost}</td>
                  <td>{v.selling_price}</td>
                  <td>{v.final_price}</td>

                  <td>{v.created_by}</td>
                  <td>{v.created_at}</td>

                  <td>{v.updated_by}</td>
                  <td>{v.updated_at}</td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>
      </div>


      {/* COST SUMMARY */}

      <div className="card mb-4">
        <div className="card-body">

          <h5 className="mb-3">Cost Summary</h5>

          <table className="table table-bordered">

            <tbody>

              <tr>
                <td>Material Cost</td>
                <td>{summary.material_cost}</td>
              </tr>

              <tr>
                <td>Machine Cost</td>
                <td>{summary.machine_cost}</td>
              </tr>

              <tr>
                <td>Labour Cost</td>
                <td>{summary.labour_cost}</td>
              </tr>

              <tr>
                <td>Overhead</td>
                <td>{summary.overhead_cost}</td>
              </tr>

              <tr className="table-light">
                <td><b>Manufacturing Cost</b></td>
                <td><b>{summary.manufacturing_cost}</b></td>
              </tr>

              <tr>
                <td>Margin</td>
                <td>{summary.margin_amount}</td>
              </tr>

              <tr className="table-light">
                <td><b>Selling Price</b></td>
                <td><b>{summary.selling_price}</b></td>
              </tr>

              <tr>
                <td>Transport</td>
                <td>{summary.transport_charge}</td>
              </tr>

              <tr>
                <td>Packing</td>
                <td>{summary.packing_charge}</td>
              </tr>

              <tr>
                <td>Insurance</td>
                <td>{summary.insurance_charge}</td>
              </tr>

              <tr className="table-light">
                <td><b>Subtotal</b></td>
                <td><b>{summary.subtotal}</b></td>
              </tr>

              <tr>
                <td>Tax</td>
                <td>{summary.tax_amount}</td>
              </tr>

              <tr className="table-success">
                <td><b>Final Price</b></td>
                <td><b>{summary.final_price}</b></td>
              </tr>

            </tbody>

          </table>

        </div>
      </div>


      {/* ITEM COST TABLE */}

      <div className="card mb-4">

        <div className="card-body">

          <h5 className="mb-3">Item Cost Breakdown</h5>

          <table className="table table-bordered">

            <thead>
              <tr>
                <th>Item</th>
                <th>Variant</th>
                <th>Equipment</th>
                <th>Qty</th>
                <th>Material</th>
                <th>Machine</th>
                <th>Labour</th>
                <th>Total</th>
              </tr>
            </thead>

            <tbody>

              {items.map((i, idx) => (

                <tr key={idx}>

                  <td>{i.item_seq_no}</td>
                  <td>{i.product_variant_name}</td>
                  <td>{i.equipment_name}</td>
                  <td>{i.quantity}</td>

                  <td>{i.material_cost}</td>
                  <td>{i.machine_cost}</td>
                  <td>{i.labour_cost}</td>
                  <td>{i.total_cost}</td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>


      {/* MATERIAL BREAKDOWN */}

      <div className="card mb-4">

        <div className="card-body">

          <h5 className="mb-3">Material Breakdown</h5>

          <table className="table table-bordered">

            <thead>
              <tr>
                <th>Material</th>
                <th>Qty</th>
                <th>Unit Cost</th>
                <th>Total Cost</th>
              </tr>
            </thead>

            <tbody>

              {materials.map((m, idx) => (

                <tr key={idx}>
                  <td>{m.description}</td>
                  <td>{m.quantity}</td>
                  <td>{m.unit_cost}</td>
                  <td>{m.total_cost}</td>
                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>


      {/* OPERATION BREAKDOWN */}

      <div className="card mb-4">

        <div className="card-body">

          <h5 className="mb-3">Operation Breakdown</h5>

          <table className="table table-bordered">

            <thead>
              <tr>
                <th>Operation</th>
                <th>Setup Time</th>
                <th>Run Time</th>
                <th>Machine Cost</th>
                <th>Labour Cost</th>
                <th>Total Cost</th>
              </tr>
            </thead>

            <tbody>

              {operations.map((o, idx) => (

                <tr key={idx}>

                  <td>{o.operation_name}</td>
                  <td>{o.setup_time}</td>
                  <td>{o.run_time}</td>

                  <td>{o.machine_cost}</td>
                  <td>{o.labour_cost}</td>
                  <td>{o.total_cost}</td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>

  );

}